import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createApplication, getApplications, getEmiCalculator, uploadApplicationSelfie } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle, FileText, Clock, IndianRupee, Calculator, Camera, RotateCcw, AlertCircle } from "lucide-react";

const TENURE_OPTIONS = [
  { value: 1, label: "1 Month" },
  { value: 2, label: "2 Months" },
  { value: 3, label: "3 Months" },
];

export default function CustomerApplication() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    amount_requested: "", tenure_months: 1, purpose: "EMI Assistance",
    reference_1_name: "", reference_1_mobile: "", reference_1_relation: "",
    reference_2_name: "", reference_2_mobile: "", reference_2_relation: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [applications, setApplications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loadingApps, setLoadingApps] = useState(true);
  // P1 Fix 8: selfie capture
  const [selfiePreview, setSelfiePreview] = useState(null);   // local blob URL
  const [selfieUrl, setSelfieUrl] = useState(null);           // backend storage URL after upload
  const [uploadingSelfie, setUploadingSelfie] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const res = await getApplications();
      setApplications(res.data.applications || []);
    } catch (err) {
      console.error(err);
    } finally { setLoadingApps(false); }
  };

  // Live breakdown — ALWAYS fetched from backend (single source of truth)
  useEffect(() => {
    const amount = parseFloat(form.amount_requested);
    if (!(amount > 0 && form.tenure_months)) { setCalculation(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await getEmiCalculator(amount, form.tenure_months);
        if (!cancelled && res.data && res.data.breakdown) {
          const b = res.data.breakdown;
          setCalculation({
            principal: b.requested_amount,
            interest_rate: 3,                     // 3% per month (fixed)
            total_interest: b.total_interest,
            processing_fee: b.processing_fee,
            total_repayable: b.total_repayable,
            monthly_emi: b.per_instalment,
            instalment_schedule: b.instalment_schedule,
            num_months: b.num_months,
          });
        }
      } catch (err) {
        if (!cancelled) setCalculation(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.amount_requested, form.tenure_months]);

  const handleSelfieCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Selfie must be smaller than 5 MB');
      return;
    }
    // Local preview
    const previewUrl = URL.createObjectURL(file);
    setSelfiePreview(previewUrl);
    // Upload immediately so the form can submit quickly
    setUploadingSelfie(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadApplicationSelfie(fd);
      if (res.data?.success && res.data.selfie_url) {
        setSelfieUrl(res.data.selfie_url);
        toast.success('Selfie uploaded');
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Selfie upload failed. Please retake.');
      setSelfiePreview(null);
      setSelfieUrl(null);
    } finally {
      setUploadingSelfie(false);
    }
    // Reset the input so the same file can be re-selected if needed
    e.target.value = '';
  };

  const retakeSelfie = () => {
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview(null);
    setSelfieUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount_requested);
    if (!amount || amount < 5000 || amount > 50000) {
      toast.error("Amount must be between ₹5,000 and ₹50,000");
      return;
    }
    // P1 Fix 8: selfie is required before submission
    if (!selfieUrl) {
      toast.error("Please take a selfie before submitting");
      return;
    }
    // Reference validations (client-side, server also validates)
    const r1m = (form.reference_1_mobile || '').replace(/\s/g, '');
    const r2m = (form.reference_2_mobile || '').replace(/\s/g, '');
    if (!form.reference_1_name.trim() || !form.reference_2_name.trim()) {
      toast.error("Enter both reference names");
      return;
    }
    if (!form.reference_1_relation || !form.reference_2_relation) {
      toast.error("Select both reference relationships");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(r1m) || !/^[6-9]\d{9}$/.test(r2m)) {
      toast.error("Reference mobiles must be valid 10-digit numbers");
      return;
    }
    if (r1m === r2m) {
      toast.error("Reference 1 and Reference 2 must be different");
      return;
    }
    setLoading(true);
    try {
      const res = await createApplication({
        amount_requested: amount,
        tenure_months: form.tenure_months,
        purpose: form.purpose || "EMI Assistance",
        reference_1_name: form.reference_1_name.trim(),
        reference_1_mobile: r1m,
        reference_1_relation: form.reference_1_relation,
        reference_2_name: form.reference_2_name.trim(),
        reference_2_mobile: r2m,
        reference_2_relation: form.reference_2_relation,
        selfie_url: selfieUrl,
      });
      toast.success("Application submitted successfully!");
      setSubmitted(true);
      const c = res.data.calculation || {};
      const b = res.data.breakdown || {};
      setCalculation({
        principal: c.principal ?? b.requested_amount,
        interest_rate: c.interest_rate_per_month ?? 3,
        total_interest: c.total_interest ?? b.total_interest,
        processing_fee: c.processing_fee ?? b.processing_fee,
        total_repayable: c.total_repayable ?? b.total_repayable,
        monthly_emi: c.monthly_emi ?? b.per_instalment,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      disbursed: "bg-blue-100 text-blue-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
        <div className="backdrop-blur-xl bg-white/70 border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl p-12 text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-2xl bg-[#ECFDF5] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-[#10B981]" strokeWidth={2} />
          </div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight mb-3" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Application Submitted!</h2>
          <p className="text-sm text-[#4B5563] leading-relaxed mb-4">Your EMI assistance application has been submitted for review.</p>
          {calculation && (
            <div className="bg-[#F0F4FF] rounded-xl p-4 text-left mb-6 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Requested amount</span><span className="font-semibold">₹{Number(calculation.principal).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Interest (3%/mo)</span><span className="font-semibold">₹{Number(calculation.total_interest).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Processing fee (2%)</span><span className="font-semibold">₹{Number(calculation.processing_fee).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
              <div className="flex justify-between border-t pt-2"><span className="text-gray-800 font-bold">Total Repayable</span><span className="font-bold text-[#1565C0]">₹{Number(calculation.total_repayable).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
              <div className="flex justify-between"><span className="text-gray-800 font-bold">Per Instalment</span><span className="font-bold text-[#10B981]">₹{Number(calculation.monthly_emi).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
            </div>
          )}
          <Button onClick={() => navigate("/customer/dashboard")} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white px-10 py-3 rounded-full font-semibold transition-all shadow-sm hover:shadow hover:-translate-y-0.5">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/customer/dashboard")} className="text-[#4B5563] hover:text-[#0A2540] transition-colors">
              <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            </button>
            <h1 className="text-lg font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>My Applications</h1>
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full px-6 text-sm">
              + New Application
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm mb-8 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0A2540]">Apply for EMI Assistance</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-sm">Cancel</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* P1 Fix 8: Selfie capture */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50/60 border border-blue-100 rounded-xl p-4" data-testid="selfie-section">
                <div className="flex items-start gap-2 mb-3">
                  <Camera className="h-5 w-5 text-[#1565C0] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-bold text-[#0A2540]">Take a Selfie</h3>
                    <p className="text-xs text-[#6B7280] mt-0.5">
                      Required for identity verification. Make sure your face is clearly visible and well-lit.
                    </p>
                  </div>
                </div>

                {!selfiePreview ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handleSelfieCapture}
                      className="hidden"
                      id="selfie-input"
                      data-testid="selfie-input"
                    />
                    <label
                      htmlFor="selfie-input"
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white border-2 border-dashed border-[#1565C0]/40 text-[#1565C0] font-semibold text-sm cursor-pointer hover:bg-[#F0F4FF] transition-colors"
                      data-testid="take-selfie-btn"
                    >
                      <Camera className="h-4 w-4" /> Take Selfie
                    </label>
                    <p className="text-[10px] text-[#6B7280] mt-2 flex items-start gap-1">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      On mobile, the camera opens automatically. On desktop, choose an image.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <img
                        src={selfiePreview}
                        alt="Selfie preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                        data-testid="selfie-preview"
                      />
                      {uploadingSelfie && (
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                          <Loader2 className="h-6 w-6 text-white animate-spin" />
                        </div>
                      )}
                      {!uploadingSelfie && selfieUrl && (
                        <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full p-1 border-2 border-white">
                          <CheckCircle className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#0A2540]">
                        {uploadingSelfie ? 'Uploading...' : selfieUrl ? 'Selfie uploaded' : 'Upload failed'}
                      </p>
                      <p className="text-xs text-[#6B7280] mt-0.5">
                        {selfieUrl ? 'Looks good! Your selfie is saved.' : 'Click retake to try again.'}
                      </p>
                      <button
                        type="button"
                        onClick={retakeSelfie}
                        disabled={uploadingSelfie}
                        className="mt-2 text-xs text-[#1565C0] font-semibold hover:underline flex items-center gap-1 disabled:opacity-50"
                        data-testid="retake-selfie-btn"
                      >
                        <RotateCcw className="h-3 w-3" /> Retake
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-2 block">Amount Required (₹5,000 - ₹50,000)</Label>
                <Input type="number" value={form.amount_requested} onChange={e => setForm({...form, amount_requested: e.target.value})} placeholder="e.g. 25000" min="5000" max="50000" className="mt-2 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-3 block">Tenure</Label>
                <div className="grid grid-cols-3 gap-3">
                  {TENURE_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setForm({...form, tenure_months: opt.value})}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${form.tenure_months === opt.value ? 'border-[#1565C0] bg-[#F0F4FF] text-[#1565C0]' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="font-bold text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-1">3%/mo</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-2 block">Purpose</Label>
                <select value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} className="w-full mt-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]">
                  <option value="EMI Assistance">EMI Assistance</option>
                  <option value="Loan Restructuring">Loan Restructuring</option>
                  <option value="Debt Consolidation">Debt Consolidation</option>
                  <option value="Emergency Funds">Emergency Funds</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* References */}
              <div className="bg-[#F9FAFB] border border-gray-200 rounded-xl p-4 space-y-4" data-testid="references-section">
                <div>
                  <h3 className="text-sm font-bold text-[#0A2540]">Reference Contacts</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Two people we can reach to verify your application. They will not be charged anything.</p>
                </div>
                {[1, 2].map(n => (
                  <div key={n} className="space-y-2" data-testid={`reference-${n}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#1565C0]">Reference {n}</p>
                    <Input
                      placeholder="Full name"
                      value={form[`reference_${n}_name`]}
                      onChange={(e) => setForm({ ...form, [`reference_${n}_name`]: e.target.value })}
                      className="rounded-lg border-gray-200 text-sm"
                      data-testid={`reference-${n}-name`}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="10-digit mobile"
                        value={form[`reference_${n}_mobile`]}
                        onChange={(e) => setForm({ ...form, [`reference_${n}_mobile`]: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        className="rounded-lg border-gray-200 text-sm font-mono"
                        data-testid={`reference-${n}-mobile`}
                      />
                      <select
                        value={form[`reference_${n}_relation`]}
                        onChange={(e) => setForm({ ...form, [`reference_${n}_relation`]: e.target.value })}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]"
                        data-testid={`reference-${n}-relation`}
                      >
                        <option value="">Relationship</option>
                        <option value="Family">Family</option>
                        <option value="Friend">Friend</option>
                        <option value="Colleague">Colleague</option>
                        <option value="Neighbour">Neighbour</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {calculation && (
                <div className="bg-gradient-to-br from-[#F0F4FF] to-[#E8F5E9] rounded-xl p-5 space-y-2 text-sm" data-testid="emi-breakdown">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="h-4 w-4 text-[#1565C0]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#1565C0]">Repayment Breakdown</span>
                  </div>
                  <div className="flex justify-between"><span className="text-gray-700">Requested amount</span><span className="font-bold">₹{Number(calculation.principal).toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between"><span className="text-gray-700">Monthly interest (3%)</span><span className="font-bold">₹{Number(calculation.principal * 0.03).toLocaleString('en-IN', {maximumFractionDigits:2})} × {form.tenure_months} mo = ₹{Number(calculation.total_interest).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="text-gray-700">Processing fee (2%)</span><span className="font-bold">₹{Number(calculation.processing_fee).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
                  <div className="border-t border-dashed border-gray-300 pt-2 flex justify-between"><span className="text-gray-800 font-bold">Total repayable</span><span className="font-bold text-[#1565C0]">₹{Number(calculation.total_repayable).toLocaleString('en-IN', {maximumFractionDigits:2})}</span></div>
                  <div className="border-t pt-3 text-center">
                    <span className="text-gray-600 text-xs">Per Instalment</span>
                    <div className="text-2xl font-black text-[#10B981]" data-testid="per-instalment-value">₹{Number(calculation.monthly_emi).toLocaleString('en-IN', {maximumFractionDigits:2})} × {form.tenure_months} mo</div>
                  </div>
                </div>
              )}

              <Button type="submit" disabled={loading || uploadingSelfie || !selfieUrl} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white py-4 rounded-full font-semibold transition-all shadow-sm hover:shadow hover:-translate-y-0.5 mt-4 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (!selfieUrl ? "Take Selfie to Submit" : "Submit Application")}
              </Button>
            </form>
          </div>
        )}

        {/* Existing Applications List */}
        <div className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">Your Applications ({applications.length})</h2>
          {loadingApps ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1565C0]" /></div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">No applications yet</p>
              {!showForm && <Button onClick={() => setShowForm(true)} className="mt-4 bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full px-6">Apply Now</Button>}
            </div>
          ) : (
            applications.map(app => (
              <div key={app.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="text-xs font-mono text-gray-400">{app.application_number || '#N/A'}</span>
                    <h3 className="font-bold text-[#0A2540] mt-1">₹{parseFloat(app.amount_requested || app.loan_amount || 0).toLocaleString()}</h3>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(app.status)}`}>
                    {app.status?.charAt(0).toUpperCase() + app.status?.slice(1)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{app.tenure || 1} month(s)</div>
                  <div className="flex items-center gap-1"><IndianRupee className="h-3 w-3" />EMI: ₹{parseFloat(app.emi_amount || 0).toLocaleString()}</div>
                  <div>{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : ''}</div>
                </div>
                {app.admin_remarks && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                    <span className="font-semibold">Remarks:</span> {app.admin_remarks}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
